import type { TranslationDictionary } from './types'

export const en: TranslationDictionary = {
  // ── Shell / header ────────────────────────────────────────────────────────
  appTitle: 'Reviewline',
  tagline: 'Agents investigate. Humans authorize.',
  webmcpActive: 'WebMCP live',
  webmcpInactive: 'WebMCP absent',
  webmcpActiveTitle: 'WebMCP tools registered on document.modelContext',
  webmcpInactiveTitle: 'WebMCP not available — using the in-page inspector',
  reset: 'Reset',
  resetDemoStateAria: 'Reset demo state',
  toolInspectorSummary: 'WebMCP Tool Inspector ({count} tools)',

  // ── Language toggle ───────────────────────────────────────────────────────
  languageToggleAria: 'Switch language to {targetLang}',
  langEn: 'EN',
  langTh: 'TH',

  // ── Authority bar ─────────────────────────────────────────────────────────
  authorityBarAria: 'Agent authority boundary',
  phaseEyebrow: 'Phase',
  phaseInvestigation: 'Investigation',
  phaseReplayReady: 'Replay ready',
  phaseAwaitingHumanDecision: 'Awaiting human decision',
  phaseDecided: 'Decision recorded',
  manifestTitle: 'Agent capability manifest',
  manifestHint: 'Registered on document.modelContext for this phase only.',
  exposureExposed: 'exposed',
  exposureWithheld: 'withheld',
  humanOnlyTitle: 'Human-only actions',
  humanOnlyNote: 'Never registered as a WebMCP tool. Least-authority product design, not a cryptographic guarantee against every form of browser actuation.',
  exposureNever: 'never registered',
  humanActionApprove: 'approve',
  humanActionReject: 'reject',
  humanActionActivate: 'activate',

  // ── Incident queue ────────────────────────────────────────────────────────
  incidentQueueTitle: 'Incidents',
  incidentQueueRegionAria: 'Incident queue',
  selectedBadge: 'Selected',

  // ── Evidence panel ────────────────────────────────────────────────────────
  evidencePanelTitle: 'Evidence',
  evidencePanelEmpty: 'Select an incident from the queue to inspect evidence.',
  evidencePanelEmptyHint: 'An agent reaches the same view by calling inspect_incident.',
  evidenceWorkspaceAria: 'Evidence workspace',
  evidenceForAria: 'Evidence for {id}',
  traceHeading: 'Agent run trace',
  traceEntryAria: '{kind} at {ts}',
  cohortHeading: 'Replay cohort',
  blockedAtLabel: 'Blocked',

  // ── Simulation view ───────────────────────────────────────────────────────
  simulationTitle: 'Guardrail patch simulation',
  simulationEmpty: 'No simulation run yet.',
  simulationEmptyHint: 'Call simulate_guardrail_patch to replay a candidate rule against the cohort.',
  simCompleted: 'Completed',
  simAria: 'Simulation {simId}',
  resultIdentity: 'Result identity',
  baselinePolicy: 'Baseline policy',
  candidatePolicy: 'Candidate policy',
  executedAt: 'Executed',
  exactRule: 'Exact rule',
  ruleLabel: 'Rule: {ruleKind}',
  thresholdLabel: 'Threshold: {threshold}',
  enforcementLabel: 'Enforcement: {enforcement}',
  noRegressions: 'No regressions detected',
  simNoRegressionDetail: 'Trigger stays blocked; benign control stays allowed.',
  simRegressionFailedTitle: 'Regression control failed',
  simRegressionFailedDetail: '{count} baseline outcome(s) changed: {cases}.',
  cohortResultsAria: 'Candidate outcome totals',
  simLabelBlocked: 'Blocked',
  simLabelAllowed: 'Allowed',
  simLabelTotal: 'Total',
  simCaseTableAria: 'Baseline and candidate case outcomes',
  simColEvidenceCase: 'Evidence case',
  simColAmount: 'Amount',
  simColTransition: 'Baseline → candidate',
  simBadgeTrigger: 'TRIGGER',
  simBadgeBenign: 'BENIGN CONTROL',
  simNotMonetary: 'Not monetary',
  counterfactualReplay: 'Counterfactual replay',

  // ── Review panel ──────────────────────────────────────────────────────────
  reviewPanelAria: 'Review panel',
  reviewPanelTitle: 'Human review panel',
  noProposalPending: 'No pending proposal.',
  noProposalDesc: 'An agent can draft one only after a replay that blocks the trigger, allows the benign control, and reports zero regressions.',
  humanReviewLine: 'Human review line',
  statusPending: 'PENDING',
  statusApproved: 'APPROVED',
  statusRejected: 'REJECTED',
  rationaleHeading: 'Rationale',
  notEligibleTitle: 'Replay evidence is not eligible for a human decision.',
  notEligibleDesc:
    'The decision controls remain unavailable. Run a fresh authoritative replay with a blocked trigger, allowed benign control, and zero regressions.',
  decisionStateHeading: 'Decision state',
  dsEnforcementOutcome: 'Enforcement outcome',
  dsSimulationStatus: 'Simulation status',
  dsHumanReview: 'Human review',
  dsIncidentState: 'Incident state',
  dsPolicyDeployment: 'Policy deployment',
  simulationUnavailable: 'Unavailable',
  consequencesTitle: 'Decision consequences',
  consequenceConfirm: 'Confirming retains the candidate {rule}; {unauthorized}.',
  consequenceReject:
    'Rejecting discards this proposal and keeps the current block in force. Neither action deploys policy or mutates an external system.',
  rulePurchaseCap: '{threshold} purchase cap',
  ruleRefundLimit: '{threshold} refund limit',
  ruleEvidenceAge: '{threshold} evidence-age limit',
  ruleFallback: 'candidate threshold',
  unauthorizedPurchase: 'the blocked purchase remains unauthorized',
  unauthorizedRefund: 'the refund remains unapproved',
  unauthorizedDeployment: 'the deployment remains blocked pending valid evidence and attestation',
  confirmActionPurchase: 'Confirm {rule} · keep purchase blocked',
  confirmActionRefund: 'Confirm {rule} · keep refund blocked',
  confirmActionDeployment: 'Confirm {rule} · keep deployment blocked',
  rejectProposalKeepBlock: 'Reject proposal · keep current block in force',
  replaySummaryLabel: 'Replay evidence · {resultId}',
  replayFieldSimulation: 'Simulation',
  replayFieldResult: 'Result',
  replayFieldTrigger: 'Trigger',
  replayFieldControl: 'Benign control',
  replayFieldRegressions: 'Regressions',
  recordedDecisionHeading: 'Recorded decision',
  recordedDecisionBody: '{status} at {ts}',
  noteLabel: 'Note',
  humanDecisionRequiredTitle: 'Human decision required.',
  requiredConfirmationsHint: 'Complete all three required confirmations to enable a decision.',
  requiredMarker: 'required',
  reviewerIdentityLabel: 'Reviewer identity',
  reviewNoteLabel: 'Review note',
  confirmEvidenceLabel: 'I reviewed the evidence, trigger, benign control, and deployment consequence.',
  decisionRecorded: 'Decision recorded',
  decisionRecordedNote: 'Human decision recorded. No external policy was deployed.',
  purchaseRemainsBlocked: 'Purchase remains blocked',
  refundRemainsBlocked: 'Refund remains blocked',
  deploymentRemainsBlocked: 'Deployment remains blocked',
  completedStatus: 'Completed',
  awaitingHumanDecision: 'Awaiting human decision',
  openStatus: 'Open',
  noExternalDeployment: 'No external deployment',

  // ── Audit log ─────────────────────────────────────────────────────────────
  auditLogTitle: 'Human decision audit',
  auditLogEmpty: 'No audit entries yet. Decisions appear here after human review.',
  auditLogAria: 'Audit log',

  // ── Session record ────────────────────────────────────────────────────────
  sessionRecordAria: 'Append-only session record',
  sessionRecordSummary: 'Session record · {count} events',
  sessionTimelineTitle: 'Session timeline',
  sessionTimelineEmpty: 'Capability changes and tool calls appear here.',
  sessionTimelineAria: 'Session timeline',

  // ── Tool inspector ────────────────────────────────────────────────────────
  toolInspectorTitle: 'Tool harness',
  toolInspectorAria: 'WebMCP tool inspector',
  registeredStatus: 'Registered ({count})',
  fallbackStatus: 'WebMCP not available — dev inspector mode',
  inputSchemaSummary: 'Input schema',
  executeTool: 'Execute',
  executingTool: 'Executing…',
  runHeroJourney: 'Run 3-step hero journey',
  runningHeroJourney: 'Running agent journey…',
  parametersLabel: 'Arguments (JSON):',
  toolOutputLabel: 'Last output:',

  // ── Guide modal ───────────────────────────────────────────────────────────
  guideButton: 'Guide & Use Cases',
  guideButtonAria: 'Open Reviewline ELI5 guide and real-world use cases',
  guideButtonTitle: 'Learn how Reviewline works, why WebMCP matters, and real-world use cases (Press ?)',
  guideModalTitle: 'Reviewline Guide: Explain Like I\'m 5 & Real-World Use Cases',
  guideModalBadge: 'Guide / ELI5',
  guideModalCloseAria: 'Close guide modal',
  guideModalClose: 'Close',
  guideSectionsAria: 'Guide sections',
  guideTabConcept: 'ELI5 Concept',
  guideTabWebmcp: 'Why WebMCP?',
  guideTabUsecases: 'Real-World Use Cases',
  guideTabTour: '3-Min Tour',
  guideConceptTitle: 'The ELI5 mental model',
  guideConceptSubtitle: 'Why autonomous AI agents cannot be given unchecked execution authority.',
  guideDetectiveTitle: 'The AI is the detective',
  guideDetectiveDesc: 'AI agents are exceptional at synthesis: reading massive log traces, cross-referencing audit records, testing counterfactual rules, and simulating whether a proposed guardrail blocks an exploit while letting legitimate traffic pass.',
  guideJudgeTitle: 'The human is the judge',
  guideJudgeDesc: 'High-consequence actions (spending money, approving policy changes, deploying code to production) require legal liability and human intent. The AI builds the evidence docket; only the human has the gavel.',
  guideWebmcpTitle: 'Why WebMCP instead of browser scraping?',
  guideWebmcpSubtitle: 'Replacing fragile DOM automation with typed, least-authority capability boundaries.',
  guideWebmcpPoint1Title: '1. Structured state synchronization',
  guideWebmcpPoint1Desc: 'Instead of parsing dense HTML or guessing button locations, the browser agent interacts with typed WebMCP tools (document.modelContext) that update the exact same reactive view the human reviewer examines.',
  guideWebmcpPoint2Title: '2. Dynamic least-authority lifecycle',
  guideWebmcpPoint2Desc: 'Tools are registered only when relevant: investigation tools first, simulation tools after inspection, and drafting only after a clean replay. When review is pending, drafting is unregistered.',
  guideWebmcpPoint3Title: '3. Enforced human boundary',
  guideWebmcpPoint3Desc: 'Approve and reject actions are never registered as WebMCP tools. They do not exist in the agent\'s tool catalog. An agent cannot call what does not exist.',
  guideUsecasesTitle: 'Four high-stakes real-world use cases',
  guideUsecasesSubtitle: 'Where this architecture applies in modern enterprise AI platforms.',
  guideUsecase1Title: '1. Procurement & expense overrides',
  guideUsecase1Desc: 'An autonomous procurement agent encounters untrusted vendor text attempting to override purchasing caps. AI traces the injection, simulates capping the transaction, and verifies normal purchases are unaffected. Human finance lead authorizes the cap.',
  guideUsecase1Demo: 'Demo equivalent: incident inc-001',
  guideUsecase2Title: '2. Customer support & refund disputes',
  guideUsecase2Desc: 'A support agent misbinds account context and attempts an over-limit refund. AI gathers customer history, tests refund threshold constraints, and drafts a review proposal. CS supervisor makes the final disbursement call.',
  guideUsecase2Demo: 'Demo equivalent: incident inc-002',
  guideUsecase3Title: '3. DevOps & CI/CD deployment gates',
  guideUsecase3Desc: 'An autonomous deployment agent tries to push a release with stale test evidence and unverified rollback states. AI flags missing health checks and proposes a deployment block. SRE engineer verifies infrastructure before proceeding.',
  guideUsecase3Demo: 'Demo equivalent: incident inc-003',
  guideUsecase4Title: '4. AI safety & jailbreak defense',
  guideUsecase4Desc: 'An enterprise chatbot experiences adversarial prompt injections. AI analyzes attack vectors, drafts a semantic guardrail patch, and validates it against a benchmark of benign customer queries to guarantee zero regressions before security leads roll it out.',
  guideUsecase4Demo: 'Applicable to enterprise LLM gateway governance',
  guideBadgeCritical: 'Critical',
  guideBadgeHigh: 'High',
  guideBadgeMedium: 'Medium',
  guideBadgeSafety: 'Safety',
  guideTourTitle: 'How to test this in 3 minutes',
  guideTourSubtitle: 'Try the full workflow yourself using the WebMCP tool harness at the bottom of the page.',
  guideTourStep1: 'Open the tool harness at the bottom of the screen and run inspect_incident for inc-001.',
  guideTourStep2: 'Run simulate_guardrail_patch with threshold 50000 to test the spending cap against the cohort.',
  guideTourStep3: 'Run draft_review_gate to create a proposal, then watch drafting disappear from the capability manifest.',
  guideTourStep4: 'As the human reviewer, enter your name, add review notes, confirm the evidence checkbox, and choose confirm or reject.',
}
